import { Box, Tooltip, Typography } from "@mui/material";
import { forwardRef, useState } from "react";
import { NavLink as RouterLink } from "react-router-dom";
import { isExternalLink } from "..";
import RoleBasedGuard from "../../../guards/RoleBasedGuard";
import useLocales from "../../../hooks/useLocales";
import { NavItemProps } from "../type";
import {
  ListItemIconStyle,
  ListItemStyle,
  ListItemStyleProps,
  ListItemTextStyle,
} from "./style";
//theme
import { useTheme } from "@mui/material/styles";
import ChevronDown from "src/assets/icon/chevron/ChevronDown";
import ChevronUpIcon from "src/assets/icon/chevron/up";
import PermissionInfo from "src/components/permissions/permissionInfo";
import NavItemPopover from "./NavItemPopover";
import NavItemSubmenuPopover from "./NavItemSubmenuPopover";
import MixpanelNavigation from "src/mixpanel/MixpanelNavigation";

// ----------------------------------------------------------------------

const TRACKED_FEATURES = ["self_training", "extraction_rules"];

// HANDLE SHOW ITEM BY ROLE
export const ListItem = forwardRef<
  HTMLDivElement & HTMLAnchorElement,
  ListItemStyleProps
>((props, ref) => (
  <RoleBasedGuard roles={props.roles}>
    <ListItemStyle {...props} ref={ref}>
      {props.children}
    </ListItemStyle>
  </RoleBasedGuard>
));

export function NavItemRoot({
  item,
  isCollapse,
  open = false,
  active,
  handleOpenModuleDialog,
  onOpen,
}: NavItemProps) {
  const { translate } = useLocales();
  const {
    title,
    path,
    icon,
    info,
    locked,
    moduleLocked,
    module,
    children,
    disabled,
    caption,
    roles,
  } = item;
  const theme = useTheme();
  const textColor = theme.palette?.text?.label;
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [submenunchorEl, setSubmenuAnchorEl] = useState<HTMLElement | null>(
    null,
  );
  const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
    handleCloseSubmenuPopover();
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };
  const handleOpenSubmenuPopover = (event: React.MouseEvent<HTMLElement>) => {
    if (locked) return;
    setSubmenuAnchorEl(event.currentTarget);
  };
  const handleCloseSubmenuPopover = () => {
    setSubmenuAnchorEl(null);
  };

  const handleClick = (e) => {
    if (moduleLocked) {
      handleOpenModuleDialog?.(module ?? "");
      return;
    }
    if (TRACKED_FEATURES.includes(title)) {
      MixpanelNavigation({ title });
    }
    // If navigateWithChildren is enabled, navigation happens via RouterLink
    // Submenu toggle happens via arrow click handler
    if (item.navigateWithChildren) {
      return;
    }
    // Default behavior: toggle submenu on click
    if (!locked) {
      onOpen?.();
    }
    if (!submenunchorEl) {
      handleOpenSubmenuPopover(e);
      handlePopoverClose();
    } else handleCloseSubmenuPopover();
  };

  const renderContent = (
    <>
      {isCollapse && icon && !submenunchorEl && (
        <NavItemPopover {...{ anchorEl, handlePopoverClose, title, locked }} />
      )}
      {isCollapse && icon && (
        <NavItemSubmenuPopover
          {...{
            anchorEl: submenunchorEl,
            handlePopoverClose: handlePopoverClose,
            submenu: children,
            title,
            locked,
            module,
            moduleLocked,
            handleOpenModuleDialog,
          }}
        />
      )}
      {icon && (
        <ListItemIconStyle
          sx={{
            ...(isCollapse
              ? {
                  mr: "0px",
                }
              : {}),
          }}
        >
          {icon}
        </ListItemIconStyle>
      )}
      <ListItemTextStyle
        sx={{
          textTransform: "capitalize", 
          color: active ? theme.palette.primary.main : textColor,
          fontWeight: active ? 700 : 400,
        }}
        disableTypography
        primary={translate(title)}
        secondary={
          <Tooltip title={translate(caption) || ""} arrow>
            <Typography
              noWrap
              variant="caption"
              component="div"
              sx={{ textTransform: "capitalize" }} 
            >
              {translate(caption)}
            </Typography>
          </Tooltip>
        }
        isCollapse={isCollapse}
      />
      {!isCollapse && (
        <Box
          sx={{
            gap: 0.5,
            display: "flex",
          }}
        >
          {info && info}
          {locked ? (
            <PermissionInfo moduleLocked={moduleLocked} />
          ) : (
            children && (
              <Box
                data-arrow-icon
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  onOpen?.();
                }}
              >
                <ArrowIcon open={open} />
              </Box>
            )
          )}
        </Box>
      )}
    </>
  );

  if (children) {
    if (item.navigateWithChildren && !isExternalLink(path)) {
      return (
        <ListItem
          {...(!locked
            ? {
                component: RouterLink,
                to: path,
                end: !children,
              }
            : {})}
          onClick={handleClick}
          onMouseEnter={handlePopoverOpen}
          onMouseLeave={handlePopoverClose}
          activeRoot={active}
          disabled={disabled}
          roles={roles}
          sx={{
            "&:hover": {
              bgcolor: "#ffffff",
            },
            "&.activeRoot": {
              bgcolor: (theme: any) =>
                `${theme.palette.text.activeTab} !important`,
            },
            "&.active": {
              bgcolor: (theme: any) =>
                `${theme.palette.text.activeTab} !important`,
            },
          }}
        >
          {renderContent}
        </ListItem>
      );
    }
    return (
      <ListItem
        onClick={handleClick}
        onMouseEnter={handlePopoverOpen}
        onMouseLeave={handlePopoverClose}
        activeRoot={active}
        disabled={disabled}
        roles={roles}
        sx={{
          "&:hover": {
            bgcolor: "#ffffff",
          },
          "&.activeRoot": {
            bgcolor: (theme: any) =>
              `${theme.palette.text.activeTab} !important`,
          },
          "&.active": {
            bgcolor: (theme: any) =>
              `${theme.palette.text.activeTab} !important`,
          },
        }}
      >
        {renderContent}
      </ListItem>
    );
  }

  return isExternalLink(path) ? (
    <ListItem
      {...(!locked
        ? {
            component: RouterLink,
            to: path,
            end: !children,
          }
        : {})}
      onMouseEnter={handlePopoverOpen}
      onMouseLeave={handlePopoverClose}
      target="_blank"
      rel="noopener"
      disabled={disabled}
      roles={roles}
      sx={{
        "&:hover": {
          bgcolor: "#ffffff",
        },
        "&.activeRoot": {
          backgroundColor: (theme: any) =>
            `${theme.palette.text.activeTab} !important`,
        },
        "&.active": {
          backgroundColor: (theme: any) =>
            `${theme.palette.text.activeTab} !important`,
        },
      }}
    >
      {renderContent}
    </ListItem>
  ) : (
    <ListItem
      {...(!locked
        ? {
            component: RouterLink,
            to: path,
            end: !children,
          }
        : {})}
      onMouseEnter={handlePopoverOpen}
      onMouseLeave={handlePopoverClose}
      activeRoot={active}
      onClick={handleClick}
      disabled={disabled}
      roles={roles}
      sx={{
        "&:hover": {
          bgcolor: "#ffffff",
        },
        "&.activeRoot": {
          bgcolor: (theme: any) => `${theme.palette.text.activeTab} !important`,
        },
        "&.active": {
          bgcolor: (theme: any) => `${theme.palette.text.activeTab} !important`,
        },
      }}
    >
      {renderContent}
    </ListItem>
  );
}

// ----------------------------------------------------------------------

type NavItemSubProps = Omit<NavItemProps, "isCollapse">;

export function NavItemSub({
  item,
  open = false,
  active = false,
  onOpen,
  handleOpenModuleDialog,
}: NavItemSubProps) {
  const { translate } = useLocales();
  const theme = useTheme();

  const {
    title,
    path,
    info,
    children,
    locked,
    color,
    disabled,
    caption,
    roles,
    moduleLocked,
    module,
  } = item;

  const renderContent = (
    <>
      <ListItemTextStyle
        primary={translate(title)}
        disableTypography
        sx={{
          textTransform: "capitalize", 
          color: active ? theme.palette.primary.main : "text.greyLight",
          fontWeight: active ? 700 : 400,
          fontSize: "12px",
        }}
        secondary={
          <Tooltip title={translate(caption) || ""} arrow>
            <Typography
              noWrap
              variant="caption"
              component="div"
              sx={{ textTransform: "initial", color: "text.greyLight" }}
            >
              {translate(caption)}
            </Typography>
          </Tooltip>
        }
      />
      {info && info}
      {locked ? (
        <PermissionInfo moduleLocked={moduleLocked} />
      ) : (
        children && <ArrowIcon open={open} />
      )}
    </>
  );

  if (children) {
    return (
      <ListItem
        onClick={(e) => {
          if (moduleLocked) {
            handleOpenModuleDialog?.(module ?? "");
            return;
          }
          if (TRACKED_FEATURES.includes(title)) {
            MixpanelNavigation({ title });
          }
          if (!locked) {
            onOpen?.();
          }
        }}
        activeSub={active}
        subItem
        disabled={disabled}
        roles={roles}
        sx={{
          pl: 5,
          "&:hover": {
            bgcolor: "#ffffff",
          },
          "&.activeRoot": {
            bgcolor: (theme: any) =>
              `${theme.palette.text.activeTab} !important`,
          },
          "&.active": {
            bgcolor: (theme: any) =>
              `${theme.palette.text.activeTab} !important`,
          },
        }}
      >
        {renderContent}
      </ListItem>
    );
  }

  return isExternalLink(path) ? (
    <ListItem
      {...(!locked
        ? {
            component: RouterLink,
            to: path,
            end: !children,
          }
        : {})}
      target="_blank"
      rel="noopener"
      subItem
      disabled={disabled}
      roles={roles}
      sx={{
        pl: 5,
        "&:hover": {
          bgcolor: "#ffffff",
        },
        "&.activeRoot": {
          bgcolor: (theme: any) => `${theme.palette.text.activeTab} !important`,
        },
        "&.active": {
          bgcolor: (theme: any) => `${theme.palette.text.activeTab} !important`,
        },
      }}
    >
      {renderContent}
    </ListItem>
  ) : (
    <ListItem
      {...(!locked
        ? {
            component: RouterLink,
            to: path,
            end: !children,
          }
        : {})}
      onClick={(e) => {
        if (moduleLocked) {
          handleOpenModuleDialog?.(module ?? "");
          return;
        }
        if (TRACKED_FEATURES.includes(title)) {
          MixpanelNavigation({ title });
        }
        if (!locked) {
          onOpen?.();
        }
      }}
      activeSub={active}
      subItem
      disabled={disabled}
      roles={roles}
      sx={{
        pl: 5,
        ml: 2,
        "&:hover": {
          bgcolor: "#ffffff",
        },
        "&.activeRoot": {
          bgcolor: (theme: any) => `${theme.palette.text.activeTab} !important`,
        },
        "&.active": {
          bgcolor: (theme: any) => `${theme.palette.text.activeTab} !important`,
        },
      }}
    >
      {renderContent}
    </ListItem>
  );
}

// ----------------------------------------------------------------------

type ArrowIconProps = {
  open: boolean;
};

export function ArrowIcon({ open }: ArrowIconProps) {
  return <>{open ? <ChevronUpIcon /> : <ChevronDown />}</>;
}
